<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import TechnologyCards from "@components/TechnologyCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getCrmPath } from "@core/schema/paths/getCrmPath.js";
	import { getDevelopmentPath } from "@core/schema/paths/getDevelopmentPath.js";
	import { getIntegrationsPath } from "@core/schema/paths/getIntegrationsPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getWebAppsPath } from "@core/schema/paths/getWebAppsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsMobileApps } from "../data/getFaqsMobileApps.js";
	import { getHowWeDevMobileApps } from "../data/getHowWeDevMobileApps.js";
	import { getMobileAppsPros } from "../data/getMobileAppsPros.js";
	import { getTechnologiesMobileApps } from "../data/getTechnologiesMobileApps.js";
	import type { MobileAppsProps } from "./MobileAppsProps.js";

	const { tenant } = getGlobalTemplateProps();
	const _: MobileAppsProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Vyvíjíme cg[[hybridní]] mobilní aplikace, které běží na cg[[iOS]] i cg[[Androidu]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Nechcete utrácet]] za samostatný vývoj pro iOS a Android? Vytvoříme vám [{getDevelopmentPath(
						tenant,
					)}][[na míru]] sp[[hybridní mobilní aplikaci]], která plynule funguje na sp[[obou]] operačních systémech."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci nezávaznou konzultaci" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8"
		gridDesc="container-6"
		centered
	>
		{#snippet headline()}
			<Formatted text="cg[[Hybridní]] mobilní aplikace aneb cg[[jedno]] řešení pro cg[[obě]] platformy" />
		{/snippet}
		{#snippet desc()}
			<cite class="pb-1 text-fstgrey-300 text-center font-italic mb-4">
				<Formatted text="„A chcete ji pro Android, nebo pro iOS?“" />
			</cite>
			<p>
				<Formatted
					text="Pokud si necháte vyvinout sp[[hybridní mobilní aplikaci]], tuto otázku od nás sp[[neuslyšíte]]. Hybridní aplikace totiž na rozdíl od tzv. sp[[nativních]] fungují na operačních systémech Android i iOS. sp[[Ušetříme]] vám tak sp[[spoustu nákladů]], základ kódu navíc můžeme použít i pro vývoj [{getWebAppsPath(
						tenant,
					)}][[webové aplikace]]."
				/>
			</p>
			<p>
				<Formatted
					text="Pokud nicméně preferujete nativní mobilní aplikaci (tzn. vývoj pro každý operační systém zvlášť), sp[[poradíme si s ní]]."
				/>
			</p>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8"
		gridDesc="container-6"
		centered
	>
		{#snippet headline()}
			<Formatted text="cg[[Výhody]] hybridního vývoje" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted text="Proč se sp[[hybridní vývoj]] vyplatí víc než klasická nativní mobilní aplikace?" />
			</p>
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getMobileAppsPros(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Nezávazně poptat" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Jak postupujeme při cg[[vývoji]] mobilních aplikací" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<p>
					<Formatted
						text="Každý projekt je trochu jiný, a proto sp[[k vývoji přistupujeme individuálně]]. Obecně ale při vývoji mobilních aplikací postupujeme takto…"
					/>
				</p>
				<Ol items={getHowWeDevMobileApps(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Mobilní aplikace na nahrávání hudby cg[[Jamujam]] nebo tvorbu AI obsahu cg[[Dazbog]]" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.MobileApps} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section
		noPadding="bot"
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Do mobilní aplikace zvládneme zakomponovat cg[[desítky funkcí]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Na míru vám kromě rozhraní aplikace sp[[přizpůsobíme i její funkce]]. Co do ní můžeme mimo jiné zakoponovat?"
				/>
			</p>
			<div class="flex flex-col md:flex-row md:gap-16">
				<ul class="custom-list">
					<li><Formatted text="Zákaznické účty" /></li>
					<li><Formatted text="Push notifikace" /></li>
					<li><Formatted text="GPS" /></li>
					<li><Formatted text="Biometrická autorizace" /></li>
					<li><Formatted text="Offline režim" /></li>
					<li><Formatted text="Platební brána" /></li>
					<li><Formatted text="Vyhledávání s filtry" /></li>
					<li><Formatted text="Chatovací okno" /></li>
				</ul>
			</div>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" gridDesc="container-6" centered>
		{#snippet headline()}
			<Formatted text="Které cg[[technologie]] při vývoji mobilních aplikací cg[[využíváme]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Používáme osvědčené technologie, na které je sp[[spoleh]] a zároveň dávají smysl i z hlediska sp[[ceny a výkonu]]."
				/>
			</p>
		{/snippet}
	</Section>
	<TechnologyCards technologies={getTechnologiesMobileApps(tenant)} />

	<Section gridHeadline="container-8" gridDesc="container-6" centered>
		{#snippet headline()}
			<Formatted text="Zajistíme cg[[propojení]] s webovou aplikací, CRM i e-shopem" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Potřebujete sp[[novou mobilní aplikaci propojit]] s dalšími firemními systémy a aplikacemi? Žádný problém, [{getIntegrationsPath(
						tenant,
					)}][[integraci]] za vás sp[[vyřešíme od A do Z]]. Mobilní aplikaci lze propojit například s sp[[ERP systémem]], [{getCrmPath(
						tenant,
					)}][[CRM systémem]], sp[[webovou aplikací]] nebo sp[[e-shopem]]."
				/>
			</p>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k vývoji cg[[mobilních aplikací]]" />
		{/snippet}
		<FaqItems faqs={getFaqsMobileApps(tenant)} />
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
